/**
 * Results Grid - Premium animation system
 * - Simple CSS grid for < 100 images (no virtualization bugs)
 * - Virtualized for 100+ images for performance
 * - Staggered entrance animations
 * - Card press micro-interactions
 * - Progressive image loading
 */

import { memo, useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import type { ImageMetadata } from '@/types';
import { ProgressiveImage } from '@/components/image/ProgressiveImage';
import { SkeletonGrid } from './SkeletonCard';
import { transitions } from '@/styles/animations'; // Used by empty state

interface ResultsGridProps {
  images: ImageMetadata[];
  onImageClick: (image: ImageMetadata) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  highlightedTags?: string[];
  className?: string;
  lastSelectedId?: string | null;
}

// Threshold for enabling virtualization
const VIRTUALIZATION_THRESHOLD = 100;

export function ResultsGrid({
  images,
  onImageClick,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  highlightedTags = [],
  className,
  lastSelectedId,
}: ResultsGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const hasRenderedOnce = useRef(false);

  // Track if this is truly the first render with images
  const isFirstRender = useMemo(() => {
    if (images.length > 0 && !hasRenderedOnce.current) {
      hasRenderedOnce.current = true;
      return true;
    }
    return false;
  }, [images]);

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    if (!hasMore || isLoading || !onLoadMore || !sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { rootMargin: '200px' } // Trigger 200px before reaching bottom
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoading, onLoadMore]);

  // Show skeleton during initial load
  if (images.length === 0 && isLoading) {
    return <SkeletonGrid count={12} className={className} />;
  }

  // Empty state
  if (images.length === 0 && !isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={transitions.cardEntrance}
        className="flex flex-col items-center justify-center py-20 text-gray-400"
      >
        <EmptyIcon />
        <p className="mt-4 text-workshop">No images found</p>
        <p className="text-workshop-sm">Try a different search or adjust filters</p>
      </motion.div>
    );
  }

  return (
    <div className={clsx('space-y-6', className)}>
      {/* Simple CSS Grid - no AnimatePresence for smooth filter transitions */}
      <div
        ref={gridRef}
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
      >
        {images.map((image, index) => (
          <EvidenceCard
            key={image.id}
            image={image}
            index={index}
            onClick={() => onImageClick(image)}
            highlightedTags={highlightedTags}
            showFocusRing={lastSelectedId === image.id}
            shouldAnimate={isFirstRender && index < 16}
          />
        ))}
      </div>

      {/* Loading skeletons for load more */}
      {isLoading && images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <motion.div
              key={`skeleton-${i}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="aspect-[4/3] rounded-xl bg-workshop-surface skeleton-shimmer"
            />
          ))}
        </div>
      )}

      {/* Infinite scroll sentinel */}
      {hasMore && <div ref={sentinelRef} className="h-4" />}

      {/* Manual load more button as fallback */}
      {hasMore && !isLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex justify-center pt-4"
        >
          <motion.button
            onClick={onLoadMore}
            whileTap={{ scale: 0.98 }}
            className={clsx(
              'px-8 py-4 rounded-xl font-semibold',
              'bg-workshop-surface text-white',
              'border border-workshop-border',
              'hover:border-workshop-accent hover:bg-workshop-border',
              'transition-colors duration-150',
              'min-h-touch text-workshop-sm'
            )}
          >
            Load More Results
          </motion.button>
        </motion.div>
      )}
    </div>
  );
}

interface EvidenceCardProps {
  image: ImageMetadata;
  index: number;
  onClick: () => void;
  highlightedTags: string[];
  showFocusRing?: boolean;
  shouldAnimate?: boolean;
}

/**
 * Evidence Card with smooth CSS transitions
 * - Press/release mechanical feel via CSS
 * - Progressive image loading
 * - Optional subtle entrance animation on first render only
 */
const EvidenceCard = memo(function EvidenceCard({
  image,
  index,
  onClick,
  highlightedTags,
  showFocusRing = false,
  shouldAnimate = false,
}: EvidenceCardProps) {
  const [isPressed, setIsPressed] = useState(false);
  const [isVisible, setIsVisible] = useState(!shouldAnimate);

  // Trigger entrance animation with stagger
  useEffect(() => {
    if (shouldAnimate) {
      const timer = setTimeout(() => setIsVisible(true), index * 20);
      return () => clearTimeout(timer);
    }
  }, [shouldAnimate, index]);

  const matchingTags = image.tags.filter((tag) =>
    highlightedTags.some((ht) => tag.toLowerCase().includes(ht.toLowerCase()))
  );

  const handlePointerDown = useCallback(() => setIsPressed(true), []);
  const handlePointerUp = useCallback(() => setIsPressed(false), []);
  const handlePointerLeave = useCallback(() => setIsPressed(false), []);

  return (
    <button
      onClick={onClick}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      className={clsx(
        'group relative flex flex-col rounded-xl overflow-hidden text-left',
        'bg-workshop-bg border border-workshop-border',
        'focus:outline-none focus:ring-2 focus:ring-workshop-accent',
        'hover:border-blue-500/50 hover:shadow-lg',
        'transition-all duration-150 ease-out',
        showFocusRing && 'focus-ring-fade',
        // Entrance animation
        shouldAnimate && !isVisible && 'opacity-0 translate-y-2',
        shouldAnimate && isVisible && 'opacity-100 translate-y-0'
      )}
      style={{
        transform: isPressed ? 'scale(0.99)' : 'scale(1)',
      }}
    >
      {/* Image container */}
      <div className="relative aspect-[4/3] bg-neutral-900 overflow-hidden">
        <ProgressiveImage
          src={image.url}
          thumbnailSrc={image.thumbnailUrl}
          alt={image.componentDetail}
          aspectRatio="4/3"
          layoutId={`image-${image.id}`}
        />

        {/* Voice reference number */}
        <div className="absolute top-2 left-2 px-2.5 py-1 bg-black/80 backdrop-blur-sm rounded font-mono text-white text-sm font-bold">
          #{index + 1}
        </div>

        {/* Confidence badge */}
        <div
          className={clsx(
            'absolute top-2 right-2 px-2 py-1 rounded font-mono text-xs font-bold',
            image.confidence >= 90 && 'bg-workshop-success text-black',
            image.confidence >= 70 && image.confidence < 90 && 'bg-workshop-warning text-black',
            image.confidence < 70 && 'bg-workshop-error text-white'
          )}
        >
          {image.confidence}%
        </div>

        {/* Before/After indicator */}
        {image.beforeImageId && (
          <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-blue-600 rounded text-xs text-white font-medium">
            HAS BEFORE
          </div>
        )}
        {image.afterImageId && (
          <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-green-600 rounded text-xs text-white font-medium">
            HAS AFTER
          </div>
        )}
      </div>

      {/* Metadata panel */}
      <div className="p-3 space-y-2 bg-workshop-surface">
        <p className="text-white font-medium text-sm leading-tight line-clamp-2">
          {image.componentDetail}
        </p>

        {/* Condition and Phase tags */}
        <div className="flex flex-wrap gap-1.5">
          <span className="px-2 py-0.5 bg-workshop-border rounded text-xs text-gray-200 capitalize">
            {image.condition.replace('-', ' ')}
          </span>
          <span className="px-2 py-0.5 bg-workshop-border rounded text-xs text-gray-400 capitalize">
            {image.phase.replace('-', ' ')}
          </span>
        </div>

        {/* Tags */}
        {image.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {matchingTags.map((tag) => (
              <span
                key={tag}
                className="px-1.5 py-0.5 bg-workshop-accent/30 border border-workshop-accent rounded text-xs text-workshop-accent font-medium"
              >
                {tag}
              </span>
            ))}
            {image.tags
              .filter((t) => !matchingTags.includes(t))
              .slice(0, 2)
              .map((tag) => (
                <span
                  key={tag}
                  className="px-1.5 py-0.5 bg-workshop-border/50 rounded text-xs text-gray-500"
                >
                  {tag}
                </span>
              ))}
          </div>
        )}

        {/* Date */}
        <p className="text-xs text-gray-500">
          {new Date(image.capturedAt).toLocaleDateString()}
        </p>
      </div>
    </button>
  );
});

function EmptyIcon() {
  return (
    <svg
      className="w-16 h-16 text-gray-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

export default ResultsGrid;
